/* nexusClient.ts */
import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getAccessToken, getRefreshToken } from '@/services/secureStorage';
import { waitForToken } from '@/utils/tokenRetry';
import { previewApolloLink } from '@/utils/previewGuard';

// Feature flag to determine auth method
const USE_COOKIE_AUTH = import.meta.env?.VITE_USE_COOKIE_AUTH === 'true';

// Use the Nexus GraphQL endpoint defined in the environment variables
const nexusHttpEndpoint = import.meta.env?.VITE_NEXUS_GRAPHQL_HTTP;

const nexusHttpLink = createHttpLink({
  uri: nexusHttpEndpoint,
  credentials: 'include' // Send cookies with requests
});

/**
 * Smart Apollo Auth Link - Optimized for Public & Authenticated Routes
 *
 * FIXES: PCV4MAXPRO-5A - "Token unavailable after exponential backoff"
 *
 * First Principles Design:
 * 1. Public routes (sponsor pages) should NEVER wait for authentication
 * 2. Authenticated users should get instant token access (0ms delay)
 * 3. Post-login race conditions need brief retry window (PCV4MAXPRO-3V fix)
 * 4. Expired sessions should fail fast, not waste 8+ seconds
 *
 * Decision Tree:
 * - Access token exists? → Use it immediately (0ms) ✅
 * - No refresh token? → Public user, skip auth (0ms) ✅
 * - Has refresh token but no access token? → Session refreshing, wait briefly (~2s) ⏱️
 *
 * Performance Impact:
 * - Public users: 8.8s → 0ms (100% faster) 🚀
 * - Authenticated users: 0ms (unchanged) ✅
 * - Post-login users: 0-500ms (acceptable) ✅
 * - Expired sessions: 8.8s → 2.2s (75% faster) ⚡
 *
 * @see https://pilotcity.sentry.io/issues/7025631294 (PCV4MAXPRO-5A)
 * @see https://pilotcity.sentry.io/issues/6993845687 (PCV4MAXPRO-3V)
 */
const nexusAuthLink = setContext(async (_, { headers }) => {
  // FAST PATH 1: Check if access token is immediately available
  // This handles 95%+ of requests (authenticated users)
  const immediateToken = getAccessToken();
  if (immediateToken) {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${immediateToken}`
      }
    };
  }

  // FAST PATH 2: Check if user has ever authenticated
  // Refresh token presence indicates user should have access token
  // Absence indicates public/unauthenticated user
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // No refresh token = never authenticated = public user
    // Send request without auth header immediately (0ms delay)
    // Backend will serve public data or return 401 if auth required
    return { headers };
  }

  // SLOW PATH: User has refresh token but no access token
  // Possible scenarios:
  // 1. Token refresh in progress (bootstrap, silent refresh)
  // 2. Race condition from login flow (PCV4MAXPRO-3V)
  // 3. Session expired (refresh token valid, access token cleared)
  //
  // Wait briefly with exponential backoff (max ~2.2 seconds)
  // This is 75% faster than previous 8.8s wait
  const result = await waitForToken({
    context: 'apollo_auth_link',
    maxAttempts: 5, // Reduced from 20: 5 attempts = ~2.2s vs 20 attempts = ~8.8s
    metadata: {
      hasRefreshToken: true,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    }
  });

  return {
    headers: {
      ...headers,
      ...(result.token ? { Authorization: `Bearer ${result.token}` } : {})
    }
  };
});

const nexusClient = new ApolloClient({
  // previewApolloLink first: a program preview (PP-3) refuses every mutation.
  link: from([previewApolloLink, nexusAuthLink, nexusHttpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      // CRITICAL: ProgramCredentials uses 'id' as ordinal index (1, 2, 3...) per program,
      // NOT as a unique identifier. Without this, Apollo incorrectly caches all credentials
      // as the same object because they all have id: 1, causing all programs to show
      // the same credential from the first cached program.
      ProgramCredentials: {
        keyFields: false // Treat as embedded object, don't normalize by id
      },
      // PublicEventType has no `id`, so Apollo could not normalise it and stored
      // every publicEvent(slug) under the same ROOT_QUERY field - where a second
      // write REPLACES the first rather than merging. Two queries do ask for the
      // same event with different selection sets: the event page takes the full
      // payload, and fetchConnectSlotMinutes takes a deliberately tiny
      // { slug, slotMinutes } so a backend without slotMinutes breaks only that
      // one call. Whichever landed last won. When it was the small one,
      // ticketCents / capacity / seatsRemaining / rsvpDeadline vanished from the
      // cache - the exact fields the seat copy falls back to FALLBACK_SEATS
      // without. Prod logs Apollo's warning about it on every board load.
      // The slug IS the identity; saying so makes the two writes merge.
      PublicEventType: {
        keyFields: ['slug']
      }
    }
  }),
  devtools: { enabled: process.env.NODE_ENV === 'development' }
});

export default nexusClient;
